import { useEffect, useMemo, useState } from 'react';
import { format, addDays } from 'date-fns';
import classNames from 'classnames';
import MailOpenIcon from 'src/assets/icons/mail-open.svg';
import { DatePicker, Input, InputTextArea, Select } from '@design-system';
import { IOption, SelectChangeVariant } from '@design-system/elements/Select/Select.types';
import { InputChangeVariant } from '@design-system/elements/Input/Input.types';
import { DatePickerChangeVariant } from '@design-system/elements/DatePicker/DatePicker.types';
import { useGetCurrencies } from 'src/shared/queries/useGetCurrencies';
import { useGetManagerContacts } from 'src/shared/queries/useGetManagerContacts';
import { useGetCompanyEntities } from 'src/shared/queries/useGetCompanyEntities';
import { useGetCompanyBusinessUnits } from 'src/shared/queries/useGetCompanyBusinessUnits';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { EmptyDateValue, DefaultDateFormat } from 'src/utils/constants/common';
import { useGetBeneficiary } from 'src/shared/queries/useGetBeneficiary';
import { IBeneficiary } from 'src/shared/types/Beneficiary';
import { useUserProfileStore } from 'src/stores/UserProfile';
import { GetBeneficiaryModifiedFields } from '../../../UserProfile.service';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from 'src/utils/constants/FeatureFlags';

const Fields = [
  'companyEntityId',
  'occupation',
  'jobPosition',
  'jobCode',
  'jobLevel',
  'jobDescription',
  'profileManagerContactId',
  'baseSalary',
  'currency',
  'frequencyOfSalary',
  'payrollLocation',
  'payrollLocationCode',
  'originalHiredDate',
  'reHiredDate',
  'terminationDate',
  'employeeId',
  'businessUnitId',
];

interface IManagerContactOption extends IOption {
  email: string;
  phoneNumber: string | number;
}

const PrincipalCompanyInfo = () => {
  const { editMode, updatedFields, setUpdatedFields } = useUserProfileStore();
  const { data: userInfo } = useGetUser();
  const { data: beneInfo } = useGetBeneficiary(userInfo?.beneId ?? 0);
  const flags = useFlags();
  const companyId = userInfo?.companyId ?? 0;
  const { data: currencies } = useGetCurrencies();
  const { data: managerContacts } = useGetManagerContacts(companyId);
  const { data: companyEntities } = useGetCompanyEntities(companyId);
  const { data: businessUnits } = useGetCompanyBusinessUnits(companyId);
  const { data: payrollLocations } = useGetReferenceData('PAYROLL_LOCATION');
  const { data: salaryFrequencies } = useGetReferenceData('SALARY_FREQUENCY');
  const [beneficiary, setBeneficiary] = useState<IBeneficiary>();
  const [selectedManagerContact, setSelectedManagerContact] = useState<IManagerContactOption>();

  useEffect(() => {
    if (beneInfo && !beneficiary) {
      setBeneficiary(JSON.parse(JSON.stringify(beneInfo)));
    }
  }, [beneInfo]);

  useEffect(() => {
    if (editMode && beneficiary && beneInfo) {
      const modifiedFields = GetBeneficiaryModifiedFields(beneInfo, beneficiary, Fields);
      const newFields = { ...updatedFields };
      const hasUpdated = Object.keys(modifiedFields)?.length > 0;
      if (hasUpdated) {
        newFields.employmentInformation = modifiedFields;
      } else {
        delete newFields.employmentInformation;
      }
      setUpdatedFields(Object.keys(newFields)?.length > 0 ? newFields : null);
    }
  }, [editMode, beneficiary, beneInfo]);

  useEffect(() => {
    if (!managerContacts || !beneInfo?.profileManagerContactId) return;

    const selectedContact = managerContacts.find(
      (contact) => contact.companyContactId === beneInfo?.profileManagerContactId
    );
    if (selectedContact) {
      setSelectedManagerContact({
        value: selectedContact.companyContactId,
        label: selectedContact.contactFullName,
        email: selectedContact.email,
        phoneNumber: selectedContact.contactPhoneNumber,
      });
    }
  }, [beneInfo?.profileManagerContactId, managerContacts]);

  const onChangeHandler = async (field: string, value: string | number | Date | null) => {
    setBeneficiary((prev) => ({ ...JSON.parse(JSON.stringify(prev)), [field]: value }));
  };

  const updateOriginalHiredDate = async (value: Date | null) => {
    const newDate = value ? format(value, DefaultDateFormat) : EmptyDateValue;
    onChangeHandler('originalHiredDate', newDate);
    onChangeHandler('reHiredDate', null);
    onChangeHandler('reHiredDate', null);
  };

  const onDateChange = async (name: string, value: Date | null) => {
    const newDate = value ? format(value, DefaultDateFormat) : EmptyDateValue;
    onChangeHandler(name, newDate);
  };

  const currenciesOptions = useMemo(
    () => currencies?.map((currency) => ({ value: currency, label: currency })) || [],
    [currencies]
  );

  const managerContactsOptions = useMemo(
    () =>
      managerContacts?.map((managerContact) => ({
        value: managerContact.companyContactId,
        label: managerContact.contactFullName,
        email: managerContact.email,
        phoneNumber: managerContact.contactPhoneNumber,
      })) || [],
    [managerContacts]
  );

  const companyEntitiesOptions = useMemo(
    () =>
      companyEntities?.map((company) => ({
        value: company.companyEntityId,
        label: company.nickName || company.entityName || '',
      })) || [],
    [companyEntities]
  );

  const businessUnitOptions = useMemo(
    () =>
      businessUnits?.map((businessUnit) => ({
        value: businessUnit.businessUnitId || '',
        label: businessUnit.businessUnitName || '',
      })) || [],
    [businessUnits]
  );

  const payrollLocationOptions = useMemo(
    () => payrollLocations?.map((location) => ({ value: location.code, label: location.description })) || [],
    [payrollLocations]
  );

  const salaryFrequencyOptions = useMemo(
    () => salaryFrequencies?.map((salary) => ({ value: salary.code, label: salary.description })) || [],
    [salaryFrequencies]
  );

  const dateFormat = userInfo?.userSettings?.dateFormat || '';

  const styles = {
    container: 'w-full flex flex-col gap-[24px] py-[24px] lg:pt-[0]',
    row: 'flex flex-col lg:flex-row gap-[24px] lg:gap-[0px]',
    col3: 'w-full lg:w-1/4 lg:pr-[24px]',
    col6: 'w-full lg:w-1/2 lg:pr-[24px]',
    col9: 'w-full lg:w-3/4 lg:pr-[24px]',
  };

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={styles.col3}>
          <Input label="Company" value={beneficiary?.companyName || ''} disabled={true} />
        </div>
        <div className={styles.col3}>
          <Select
            options={companyEntitiesOptions}
            label="Employing Entity"
            selectedOption={{
              value: beneficiary?.companyEntityId as number,
              label: beneficiary?.companyEntity as string,
            }}
            searchable
            showClear={beneficiary?.companyEntityId ? true : false}
            onChange={(option: IOption | null) => onChangeHandler('companyEntityId', option?.value || -1)}
            hasChanged={
              companyEntitiesOptions?.length > 0 &&
              (beneInfo?.companyEntityId || '') !== (beneficiary?.companyEntityId || '')
            }
            disabled={!editMode}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Input
            label="Occupation"
            value={beneficiary?.occupation}
            onChange={(event) => onChangeHandler('occupation', event.target.value)}
            hasChanged={(beneInfo?.occupation || '') !== (beneficiary?.occupation || '')}
            disabled={!editMode}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.col3}>
          <Input
            label="Job Title"
            value={beneficiary?.jobPosition}
            onChange={(event) => onChangeHandler('jobPosition', event.target.value)}
            hasChanged={(beneInfo?.jobPosition || '') !== (beneficiary?.jobPosition || '')}
            disabled={!editMode}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Input
            label="Job Code"
            value={beneficiary?.jobCode}
            onChange={(event) => onChangeHandler('jobCode', event.target.value)}
            hasChanged={(beneInfo?.jobCode || '') !== (beneficiary?.jobCode || '')}
            disabled={!editMode}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Input
            label="Job Level / Grade"
            value={beneficiary?.jobLevel}
            onChange={(event) => onChangeHandler('jobLevel', event.target.value)}
            hasChanged={(beneInfo?.jobLevel || '') !== (beneficiary?.jobLevel || '')}
            disabled={!editMode}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
      </div>
      <div className={styles.col9}>
        <InputTextArea
          className="min-h-[200px]"
          label="Job Description"
          value={beneficiary?.jobDescription}
          onChange={(event) => onChangeHandler('jobDescription', event.target.value)}
          hasChanged={(beneInfo?.jobDescription || '') !== (beneficiary?.jobDescription || '')}
          disabled={!editMode}
          inputChangedStyle={InputChangeVariant.Secondary}
        />
      </div>
      <div className={styles.row}>
        <div className={styles.col3}>
          <Select
            label="Manager"
            options={managerContactsOptions}
            selectedOption={{
              label: selectedManagerContact?.label || '',
              value: selectedManagerContact?.value as number,
            }}
            searchable
            showClear={selectedManagerContact?.value ? true : false}
            onChange={(option) => {
              setSelectedManagerContact(managerContactsOptions.find((contact) => contact.value === option?.value));
              onChangeHandler('profileManagerContactId', option?.value || -1);
            }}
            hasChanged={
              managerContactsOptions?.length > 0 &&
              (beneInfo?.profileManagerContactId || '') !== (beneficiary?.profileManagerContactId || '')
            }
            disabled={!editMode}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Input
            icon={<img src={MailOpenIcon} />}
            iconPosition="right"
            iconClickable={!!selectedManagerContact?.email}
            onIconClick={() => {
              if (selectedManagerContact?.email) window.location.href = `mailto:${selectedManagerContact?.email}`;
            }}
            label="Manager Email Address"
            value={selectedManagerContact?.email || ''}
            disabled
          />
        </div>
        <div className={styles.col3}>
          <Input
            label="Manager Phone Number"
            value={String(selectedManagerContact?.phoneNumber ?? '') || ''}
            disabled
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={classNames(styles.col3, 'flex')}>
          <div className="flex-1">
            <Input
              className="!border-r-0"
              label="Base Salary"
              value={beneficiary?.baseSalary}
              onChange={(event) => onChangeHandler('baseSalary', event.target.value || 0)}
              hasChanged={(beneInfo?.baseSalary || '') !== (beneficiary?.baseSalary || '')}
              disabled={!editMode}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
          <Select
            className="w-[124px]"
            options={currenciesOptions}
            label="Currency"
            selectedOption={{ value: beneficiary?.currency || '', label: beneficiary?.currency || '' }}
            showClear={beneficiary?.currency ? true : false}
            onChange={(option: IOption | null): void => {
              if (option?.value == beneficiary?.currency) return;
              onChangeHandler('currency', option?.value || '');
            }}
            hasChanged={currenciesOptions?.length > 0 && (beneInfo?.currency || '') !== (beneficiary?.currency || '')}
            disabled={!editMode}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Select
            options={salaryFrequencyOptions}
            label="Per"
            selectedOption={{
              value: beneficiary?.frequencyOfSalary || '',
              label: beneficiary?.frequencyOfSalary || '',
            }}
            searchable
            showClear={beneficiary?.frequencyOfSalary ? true : false}
            onChange={(option: IOption | null): void => {
              if (option?.value === beneficiary?.frequencyOfSalary) return;
              onChangeHandler('frequencyOfSalary', option?.value || '');
            }}
            hasChanged={
              salaryFrequencyOptions?.length > 0 &&
              (beneInfo?.frequencyOfSalary || '') !== (beneficiary?.frequencyOfSalary || '')
            }
            disabled={!editMode}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Select
            options={payrollLocationOptions}
            label="Payroll Location"
            selectedOption={{
              value: beneficiary?.payrollLocationCode || '',
              label: beneficiary?.payrollLocation || '',
            }}
            searchable
            showClear={beneficiary?.payrollLocation ? true : false}
            onChange={(option: IOption | null): void => {
              if (option?.value === beneficiary?.payrollLocationCode) return;
              onChangeHandler('payrollLocation', option?.value || '');
              onChangeHandler('payrollLocationCode', option?.value || '');
            }}
            hasChanged={
              payrollLocationOptions?.length > 0 &&
              (beneInfo?.payrollLocation || '') !== (beneficiary?.payrollLocation || '')
            }
            disabled={!editMode}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.col3}>
          <DatePicker
            value={beneficiary?.originalHiredDate === EmptyDateValue ? undefined : beneficiary?.originalHiredDate}
            dateFormat={dateFormat}
            label="Original Hired Date"
            onChange={(value) => updateOriginalHiredDate(value)}
            hasChanged={(beneInfo?.originalHiredDate || '') !== (beneficiary?.originalHiredDate || '')}
            disabled={!editMode}
            datePickerChangedStyle={DatePickerChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <DatePicker
            value={beneficiary?.reHiredDate === EmptyDateValue ? undefined : beneficiary?.reHiredDate}
            dateFormat={dateFormat}
            minDate={beneficiary?.originalHiredDate ? addDays(beneficiary.originalHiredDate, 2) : undefined}
            label="Re-Hired Date"
            onChange={(value) => onDateChange('reHiredDate', value)}
            hasChanged={(beneInfo?.reHiredDate || '') !== (beneficiary?.reHiredDate || '')}
            disabled={!editMode}
            datePickerChangedStyle={DatePickerChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          {!flags[FeatureFlags.HideTerminationDate] && (
            <DatePicker
              value={beneficiary?.terminationDate === EmptyDateValue ? undefined : beneficiary?.terminationDate}
              dateFormat={dateFormat}
              minDate={beneficiary?.originalHiredDate ? addDays(beneficiary.originalHiredDate, 2) : undefined}
              label="Termination Date"
              onChange={(value) => onDateChange('terminationDate', value)}
              hasChanged={(beneInfo?.terminationDate || '') !== (beneficiary?.terminationDate || '')}
              disabled={!editMode}
              datePickerChangedStyle={DatePickerChangeVariant.Secondary}
            />
          )}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.col3}>
          <Input
            label="Employee ID"
            value={`${beneficiary?.employeeId}`}
            onChange={(event) => onChangeHandler('employeeId', event.target.value)}
            hasChanged={(beneInfo?.employeeId || '') !== (beneficiary?.employeeId || '')}
            disabled={!editMode}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <Select
            options={businessUnitOptions}
            label="Business Unit"
            selectedOption={{ value: beneficiary?.businessUnitId || '', label: beneficiary?.businessUnit || '' }}
            searchable
            showClear={beneficiary?.businessUnitId ? true : false}
            onChange={(option: IOption | null): void => {
              if (option?.value === beneficiary?.businessUnitId) return;
              onChangeHandler('businessUnitId', option?.value || -1);
            }}
            hasChanged={
              businessUnitOptions?.length > 0 &&
              (beneInfo?.businessUnitId || '') !== (beneficiary?.businessUnitId || '')
            }
            disabled={!editMode}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
      </div>
    </div>
  );
};

export default PrincipalCompanyInfo;
